import React, {useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';
import {ModelDTO, ModelDetailDTO} from 'hemwb-api';
import SubmitButton from '../../../../core/Buttons/SubmitButton';
import {Connect16} from '@carbon/icons-react';
import {tid} from '../../../../../testUtils';
import ModelRequestAccessDialog from './ModelRequestAccessDialog';
import {IconButton} from '@mui/material';
import {Button} from 'antd';
import {messageAdd, MessageTypes} from '../../../../../store/message';
import {isModelTheorem} from '../../../../../store/model';
import {joinWithAnd} from '../../../../../store/model/utils';
import {ButtonType} from 'antd/es/button';
import {LockFilled} from '@ant-design/icons';

type ModelRequestAccessButtonProps = {
  model: ModelDTO;
  fromDynamicSearch?: boolean;
  displayAntDesign?: boolean;
  modelDetail?: ModelDetailDTO;
  type?: ButtonType;
  width?: string;
  icon?: boolean;
};

const ModelRequestAccessButton: React.FC<ModelRequestAccessButtonProps> = ({
  model,
  fromDynamicSearch,
  displayAntDesign,
  modelDetail,
  type,
  width,
  icon,
}) => {
  const [open, setOpen] = useState(false);
  const [requested, setRequested] = useState(false);
  const dispatch = useDispatch();
  const theoremModel = isModelTheorem(model);

  const handleSuccess = useCallback(() => {
    setOpen(false);
    setRequested(true);
  }, []);

  const handleCancel = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSecurityAPI = useCallback(
    (e?: React.MouseEvent) => {
      // Stop propagation to prevent row click in tables
      if (e) {
        e.stopPropagation();
      }

      if (theoremModel) {
        if (modelDetail && modelDetail.permissionsTH) {
          if (
            modelDetail.permissionsTH['model_access'] === 'false' &&
            modelDetail.permissionsTH['allow_access'] === 'true' &&
            modelDetail.permissionsTH['data_cut_access'] === 'true' &&
            modelDetail.permissionsTH['country_access'] === 'true'
          ) {
            return dispatch(
              messageAdd(
                ' This is a restricted model version, please confirm your access with the HEDS Lead.',
                MessageTypes.ERROR,
              ),
            );
          }

          let errorMessage = "You don't have ";
          let restrictedMessage = ' For restricted models, confirm your access with the HEDS Lead.';
          const errorObjects = [];
          if (modelDetail.permissionsTH['allow_access'] === 'false') {
            errorObjects.push('Trial');
          }
          if (modelDetail.permissionsTH['data_cut_access'] === 'false') {
            errorObjects.push('Database Lock');
          }
          if (modelDetail.permissionsTH['country_access'] === 'false') {
            errorObjects.push('Country');
          }
          errorMessage += joinWithAnd(errorObjects);

          if (modelDetail.permissionsTH['model_access'] === 'false') {
            restrictedMessage = ' This is a restricted model version, please confirm your access with the HEDS Lead.';
          }
          dispatch(
            messageAdd(
              errorMessage +
                ` access in THEOREM. <div style="margin: 10px 0px;"> Please request access through the  below link: <a href="https://collaboration.merck.com/sites/THEOREM/Lists/FAQ%20%20THEOREM/DispForm.aspx?ID=53&e=mvH1Me">THEOREM Access Guide</a></div>` +
                restrictedMessage,
              MessageTypes.ERROR,
            ),
          );
        } else {
          setOpen(false);
        }
      } else {
        setOpen(true);
      }
    },
    [dispatch, modelDetail, theoremModel],
  );

  return (
    <>
      {displayAntDesign ? (
        <Button
          type={type ?? 'link'}
          onClick={handleSecurityAPI}
          disabled={requested}
          {...tid('model-search-request-access')}
          style={{width: width ?? 'auto'}}
          icon={icon && <LockFilled />}
        >
          Request Access
        </Button>
      ) : fromDynamicSearch ? (
        <IconButton
          aria-label="Request Access"
          size="small"
          onClick={handleSecurityAPI}
          disabled={requested}
          className="icon32"
          {...tid('model-card-request-access')}
        >
          <Connect16 />
        </IconButton>
      ) : (
        modelDetail && (
          <SubmitButton
            onClick={handleSecurityAPI}
            disabled={requested}
            type="button"
            endIcon={<Connect16 />}
            {...tid('btn-request-access')}
          >
            Request Access
          </SubmitButton>
        )
      )}

      {open && (
        <ModelRequestAccessDialog
          model={model}
          open={open && !requested}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      )}
    </>
  );
};

export default ModelRequestAccessButton;
